/**
 * @license
 * SPDX-License-Identifier: Apache-2.5
 */

import React, { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import CatalogView from './components/CatalogView';
import AuthModal from './components/AuthModal';
import ResetPassword from './components/ResetPassword';
import GoogleSetup from './components/GoogleSetup';
import { supabase } from './lib/supabase';
import { Vendor } from './types';

type View = 'landing' | 'dashboard' | 'catalog' | 'reset-password' | 'google-setup';

export default function App() {
  const [view, setView]                     = useState<View>('landing');
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [catalogName, setCatalogName]       = useState('');
  const [session, setSession]               = useState<Session | null>(null);
  const [authModal, setAuthModal]           = useState<'login' | 'register' | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  // ── Auth state listener ───────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session?.user) await resolveVendorFromSession(session);
      setSessionLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);

      if (event === 'SIGNED_IN' && session?.user) {
        // Google OAuth callback — check if vendor exists
        const vendor = await resolveVendorFromSession(session);
        if (!vendor) {
          // New Google user — needs to set up catalog details
          window.history.replaceState(null, '', '/setup');
          setView('google-setup');
        } else {
          window.history.replaceState(null, '', '/dashboard');
          setView('dashboard');
        }
      }

      if (event === 'SIGNED_OUT') {
        setSelectedVendor(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Resolve vendor from session email ─────────────────────────────────────
  async function resolveVendorFromSession(session: Session): Promise<Vendor | null> {
    const { data: vendor } = await supabase
      .from('vendors')
      .select('*')
      .eq('email', session.user.email!)
      .single();

    if (vendor) setSelectedVendor(vendor);
    return vendor ?? null;
  }

  // ── Client-side routing ───────────────────────────────────────────────────
  useEffect(() => {
    if (sessionLoading) return;

    const handleRouting = async () => {
      const path  = window.location.pathname;
      const parts = path.split('/').filter(Boolean);

      if (parts.length === 0) {
        setView('landing');
      } else if (parts[0] === 'reset-password') {
        setView('reset-password');
      } else if (parts[0] === 'setup') {
        setView('google-setup');
      } else if (parts[0] === 'auth' && parts[1] === 'callback') {
        // Google OAuth redirects here — Supabase handles the token
        // onAuthStateChange above will fire and handle routing
        setView('landing');
      } else if (parts[0] === 'dashboard') {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          window.history.replaceState(null, '', '/');
          setView('landing');
          setAuthModal('login');
        } else {
          if (!selectedVendor) await resolveVendorFromSession(session);
          setView('dashboard');
        }
      } else {
        setCatalogName(parts[0]);
        setView('catalog');
      }
    };

    handleRouting();
    window.addEventListener('popstate', handleRouting);
    return () => window.removeEventListener('popstate', handleRouting);
  }, [sessionLoading]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const navigateTo = (newView: 'landing' | 'dashboard' | 'catalog', extra?: { catalogName?: string }) => {
    let url = '/';
    if (newView === 'dashboard') {
      if (!session) { setAuthModal('login'); return; }
      url = '/dashboard';
    } else if (newView === 'catalog') {
      url = `/${extra?.catalogName ?? ''}`;
      setCatalogName(extra?.catalogName ?? '');
    }
    window.history.pushState(null, '', url);
    setView(newView);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSelectedVendor(null);
    window.history.pushState(null, '', '/');
    setView('landing');
  };

  const handleAuthSuccess = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setAuthModal(null);
    window.history.pushState(null, '', '/dashboard');
    setView('dashboard');
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const renderView = () => {
    switch (view) {
      case 'reset-password':
        return (
          <ResetPassword onDone={() => { window.history.pushState(null, '', '/'); setView('landing'); setAuthModal('login'); }} />
        );

      case 'google-setup':
        return session?.user ? (
          <GoogleSetup
            email={session.user.email!}
            authUserId={session.user.id}
            onSuccess={vendor => {
              setSelectedVendor(vendor);
              window.history.pushState(null, '', '/dashboard');
              setView('dashboard');
            }}
          />
        ) : null;

      case 'dashboard':
        return (
          <Dashboard
            initialVendor={selectedVendor || undefined}
            onSelectVendor={v => setSelectedVendor(v)}
            onBackToHome={() => navigateTo('landing')}
            onSignOut={handleSignOut}
          />
        );

      case 'catalog':
        return <CatalogView businessName={catalogName} onBackToHome={() => navigateTo('landing')} />;

      case 'landing':
      default:
        return (
          <LandingPage
            session={session}
            currentVendor={selectedVendor}
            onOpenDashboard={() => navigateTo('dashboard')}
            onOpenCatalog={name => navigateTo('catalog', { catalogName: name })}
            onOpenAuth={mode => setAuthModal(mode)}
            onSignOut={handleSignOut}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] relative overflow-hidden flex flex-col font-sans text-slate-900 selection:bg-orange-100">
      <div className="absolute top-[-100px] right-[-100px] w-96 h-96 bg-yellow-300/30 rounded-full blur-3xl pointer-events-none z-0" />
      <div className="absolute bottom-[-100px] left-[-100px] w-96 h-96 bg-emerald-300/30 rounded-full blur-3xl pointer-events-none z-0" />
      <div className="relative z-10 flex-1 flex flex-col">{renderView()}</div>
      {authModal && (
        <AuthModal mode={authModal} onClose={() => setAuthModal(null)} onSuccess={handleAuthSuccess} />
      )}
    </div>
  );
}
